import React, { useEffect, useMemo, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { InputSwitch } from "primereact/inputswitch";
import { MultiSelect } from "primereact/multiselect";
import { toast } from "react-toastify";

import api from "../api/axios";
import "../assets/styles/page.css";
import "../assets/styles/notificationControl.css";

const PAGE_NAME = "Notification Control";

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const showRequestError = (error, fallbackMessage) => {
  if (error.response?.status === 403) return;
  toast.error(error.response?.data?.message || fallbackMessage);
};

const NotificationControl = () => {
  const [routingRules, setRoutingRules] = useState([]);
  const [routingOptions, setRoutingOptions] = useState({ roles: [], users: [] });
  const [loading, setLoading] = useState(true);
  const [savingRule, setSavingRule] = useState(null);

  const roleOptions = useMemo(
    () =>
      routingOptions.roles.map((role) => ({
        label: role.role_name,
        value: Number(role.role_id),
      })),
    [routingOptions.roles],
  );

  const userOptions = useMemo(
    () =>
      routingOptions.users.map((admin) => ({
        label: `${admin.name} (${admin.email})`,
        value: Number(admin.user_id),
      })),
    [routingOptions.users],
  );

  const fetchRoutingConfiguration = async () => {
    try {
      setLoading(true);
      const [rulesResponse, optionsResponse] = await Promise.all([
        api.get("/admin/notifications/routing", {
          headers: getPermissionHeaders("view"),
        }),
        api.get("/admin/notifications/routing/options", {
          headers: getPermissionHeaders("view"),
        }),
      ]);

      setRoutingRules(rulesResponse.data?.data || []);
      setRoutingOptions(optionsResponse.data?.data || { roles: [], users: [] });
    } catch (error) {
      showRequestError(error, "Failed to load notification controls.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoutingConfiguration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeRoutingRule = (eventKey, field, value) => {
    setRoutingRules((previous) =>
      previous.map((rule) =>
        rule.event_key === eventKey ? { ...rule, [field]: value } : rule,
      ),
    );
  };

  const saveRoutingRule = async (rule) => {
    try {
      setSavingRule(rule.event_key);
      await api.put(
        `/admin/notifications/routing/${encodeURIComponent(rule.event_key)}`,
        {
          primary_role_id: rule.primary_role_id || null,
          primary_user_ids: rule.primary_user_ids || [],
          supervisor_role_id: rule.supervisor_role_id || null,
          supervisor_user_ids: rule.supervisor_user_ids || [],
          email_enabled: Boolean(rule.email_enabled),
          is_active: true,
        },
        { headers: getPermissionHeaders("edit") },
      );

      toast.success(`${rule.label} routing saved.`);
      await fetchRoutingConfiguration();
    } catch (error) {
      showRequestError(error, "Failed to save notification control.");
    } finally {
      setSavingRule(null);
    }
  };

  return (
    <section className="admin-page notification-control-page">
      <div className="page-header">
        <div>
          <p>System configuration</p>
          <h1>Notification Control</h1>
        </div>
      </div>

      <div className="page-card notification-routing-card">
        <div className="notification-routing-heading">
          <div>
            <p>Delivery rules</p>
            <h2>Notification &amp; Email Routing</h2>
            <span>
              Choose the handler and supervision recipients for each business
              event. Email delivery can be enabled independently per rule.
            </span>
          </div>
        </div>

        {loading ? (
          <div className="routing-loading">Loading notification controls...</div>
        ) : routingRules.length === 0 ? (
          <div className="routing-loading">No routing rules are available.</div>
        ) : (
          <div className="routing-rule-list">
            {routingRules.map((rule) => (
              <div className="routing-rule" key={rule.event_key}>
                <div className="routing-rule-title">
                  <strong>{rule.label}</strong>
                  <small>{rule.event_key}</small>
                </div>

                <div className="routing-field">
                  <label>Handler role</label>
                  <Dropdown
                    value={rule.primary_role_id || null}
                    options={roleOptions}
                    onChange={(event) =>
                      changeRoutingRule(rule.event_key, "primary_role_id", event.value)
                    }
                    placeholder="All admins (fallback)"
                    showClear
                  />
                </div>

                <div className="routing-field">
                  <label>Specific handlers</label>
                  <MultiSelect
                    value={rule.primary_user_ids || []}
                    options={userOptions}
                    onChange={(event) =>
                      changeRoutingRule(rule.event_key, "primary_user_ids", event.value)
                    }
                    placeholder="Select users"
                    display="chip"
                    filter
                  />
                </div>

                <div className="routing-field">
                  <label>Supervision role</label>
                  <Dropdown
                    value={rule.supervisor_role_id || null}
                    options={roleOptions}
                    onChange={(event) =>
                      changeRoutingRule(
                        rule.event_key,
                        "supervisor_role_id",
                        event.value,
                      )
                    }
                    placeholder="No supervising role"
                    showClear
                  />
                </div>

                <div className="routing-field">
                  <label>Specific supervisors</label>
                  <MultiSelect
                    value={rule.supervisor_user_ids || []}
                    options={userOptions}
                    onChange={(event) =>
                      changeRoutingRule(
                        rule.event_key,
                        "supervisor_user_ids",
                        event.value,
                      )
                    }
                    placeholder="Select supervisors"
                    display="chip"
                    filter
                  />
                </div>

                <div className="routing-actions">
                  <label>
                    <InputSwitch
                      checked={Boolean(rule.email_enabled)}
                      onChange={(event) =>
                        changeRoutingRule(rule.event_key, "email_enabled", event.value)
                      }
                    />
                    <span>Send email too</span>
                  </label>

                  <button
                    type="button"
                    className="primary-action"
                    onClick={() => saveRoutingRule(rule)}
                    disabled={savingRule === rule.event_key}
                  >
                    {savingRule === rule.event_key ? "Saving..." : "Save Rule"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default NotificationControl;
